import React from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Section } from "../components/PageShell";
import { ArrowRight } from "lucide-react";
import { HOME, BUSINESS_AREAS, BRAND } from "../data/site";

const Home = () => (
    <>
        {/* HERO */}
        <section className="ns-cross-pattern border-b border-[var(--ns-border)]">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-44">
                <div className="max-w-3xl mx-auto text-center">
                    <Eyebrow>{HOME.eyebrow}</Eyebrow>
                    <h1
                        className="font-display mt-8 text-4xl sm:text-5xl lg:text-[80px] font-bold leading-[0.98] tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                        data-testid="home-title"
                    >
                        {HOME.title}
                    </h1>
                    <p
                        className="font-body mt-10 text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto"
                        style={{ color: "var(--ns-body)" }}
                        data-testid="home-description"
                    >
                        {HOME.description}
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/business-areas"
                            className="ns-btn-primary"
                            data-testid="home-cta-areas"
                        >
                            Business Areas <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/contact"
                            className="ns-btn-ghost"
                            data-testid="home-cta-contact"
                        >
                            Business Enquiries
                        </Link>
                    </div>
                </div>
            </div>
        </section>

        {/* Business Areas grid */}
        <Section>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                <div>
                    <Eyebrow>Operating across multiple sectors</Eyebrow>
                    <h2
                        className="font-display mt-5 text-3xl lg:text-5xl font-bold leading-tight tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Business Areas
                    </h2>
                </div>
                <Link
                    to="/business-areas"
                    className="font-body text-sm font-medium inline-flex items-center gap-2 hover:underline"
                    style={{ color: "var(--ns-ink)" }}
                >
                    Learn more <ArrowRight size={14} />
                </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ns-border)] border border-[var(--ns-border)]">
                {BUSINESS_AREAS.map((b) => (
                    <Link
                        key={b.slug}
                        to={`/business-areas/${b.slug}`}
                        className="group bg-white p-8 lg:p-10 hover:bg-[#fafaf8] transition-colors flex flex-col"
                        data-testid={`home-area-${b.slug}`}
                    >
                        <h3
                            className="font-display text-2xl font-bold mb-3 tracking-tight"
                            style={{ color: "var(--ns-ink)" }}
                        >
                            {b.title}
                        </h3>
                        <p
                            className="font-body text-sm flex-1"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            {b.teaser}
                        </p>
                        <span
                            className="mt-8 font-body text-sm font-medium inline-flex items-center gap-2 transition-transform group-hover:translate-x-1"
                            style={{ color: "var(--ns-ink)" }}
                        >
                            Learn more <ArrowRight size={14} />
                        </span>
                    </Link>
                ))}
            </div>
        </Section>

        {/* Global Reach */}
        <section className="border-t border-[var(--ns-border)] bg-[#fafaf8]">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <Eyebrow>Global Reach</Eyebrow>
                        <h2
                            className="font-display mt-5 text-3xl lg:text-5xl font-bold leading-tight tracking-tight"
                            style={{ color: "var(--ns-ink)" }}
                        >
                            Cross-border trade
                            <br />
                            and strategic partnerships.
                        </h2>
                        <p
                            className="font-body mt-8 text-base leading-relaxed max-w-md"
                            style={{ color: "var(--ns-body)" }}
                        >
                            Facilitating cross-border trade and strategic partnerships across Asia-Pacific, Americas, Middle East, Europe, and Africa.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-8 max-w-md border-t border-[var(--ns-border)] pt-8">
                            <Stat n={HOME.statRegions} label="Regions" />
                            <Stat n={HOME.statSectors} label="Sectors" />
                            <Stat n="∞" label="Commitment" />
                        </div>
                        <Link
                            to="/global-presence"
                            className="mt-10 inline-flex items-center gap-2 font-body text-sm font-medium hover:underline"
                            style={{ color: "var(--ns-ink)" }}
                        >
                            Learn more <ArrowRight size={14} />
                        </Link>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-2 gap-4">
                        <img
                            src={HOME.images[0]}
                            alt="Global trade"
                            className="w-full aspect-[4/5] object-cover grayscale"
                            loading="lazy"
                        />
                        <img
                            src={HOME.images[1]}
                            alt="Industrial steel structure"
                            className="w-full aspect-[4/5] object-cover grayscale mt-10"
                            loading="lazy"
                        />
                    </div>
                </div>
            </div>
        </section>

        {/* Approach */}
        <Section>
            <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <Eyebrow>Our Approach</Eyebrow>
                    <h2
                        className="font-display mt-5 text-3xl lg:text-5xl font-bold leading-tight tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Integrity, reliability,
                        <br />
                        and long-term partnerships.
                    </h2>
                </div>
                <div className="lg:col-span-7">
                    <p
                        className="font-body text-lg leading-relaxed max-w-2xl"
                        style={{ color: "var(--ns-body)" }}
                    >
                        {HOME.approachLead}
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {HOME.approach.map((v) => (
                            <span
                                key={v}
                                className="inline-flex items-center px-5 py-3 border border-[var(--ns-ink)] font-body text-sm font-medium"
                                style={{ color: "var(--ns-ink)" }}
                            >
                                {v}
                            </span>
                        ))}
                    </div>
                    <Link
                        to="/values"
                        className="mt-10 inline-flex items-center gap-2 font-body text-sm font-medium hover:underline"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Learn more <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </Section>

        {/* Enquiries strip */}
        <section className="border-t border-[var(--ns-border)] bg-[var(--ns-ink)]">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                <div>
                    <span
                        className="font-body text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "rgba(255,255,255,0.6)" }}
                    >
                        Business Enquiries
                    </span>
                    <h2
                        className="font-display mt-3 text-3xl lg:text-5xl font-bold leading-tight tracking-tight"
                        style={{ color: "#fff" }}
                    >
                        Get in touch with {BRAND.short}.
                    </h2>
                </div>
                <Link
                    to="/contact"
                    className="inline-flex items-center gap-3 bg-white text-[var(--ns-ink)] px-7 py-4 font-body text-sm font-medium hover:bg-[#f0f0ec] transition-colors"
                    data-testid="home-enquiries-cta"
                >
                    Contact <ArrowRight size={16} />
                </Link>
            </div>
        </section>
    </>
);

const Stat = ({ n, label }) => (
    <div>
        <div
            className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
            style={{ color: "var(--ns-ink)" }}
        >
            {n}
        </div>
        <div
            className="font-body text-xs uppercase tracking-[0.18em] mt-2"
            style={{ color: "var(--ns-muted)" }}
        >
            {label}
        </div>
    </div>
);

export default Home;
